import { useDispatch } from '@tabeeb/store'

import { callApiAsync } from '@tabeeb/shared/utils/requests'
import { onAddSuccessNotification, onAddErrorNotification } from '@tabeeb/modules/notification/actions'
import getErrorMessageFromResponse from '@tabeeb/modules/shared/utils/getErrorMessageFromResponse'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { InvitesQueryKeys } from '@tabeeb/modules/invites/requests'
import {
  ContentUserRole,
  InvitesCreateRequest,
  InviteWithQualificationsResponse,
} from '@tabeeb/contracts/main-api/swagger-main-api'
import { AxiosError } from 'axios'
import { useApi } from '@tabeeb/shared/utils/API'
import {
  copyInviteLinkFailMessage,
  copyInviteLinkSuccessMessage,
  resendInviteFailMessage,
  resendInviteSuccessMessage,
  inviteSuccessMessage,
} from '../common/notificationMessages'

import {
  getInviteUrlById,
  resendInvite,
  revokeQuickAccessUrl,
  updateInviteRole,
  updateInviteTime,
  updateQuickAccessUrlPassword,
} from '../actions'

interface UseContentInvitesProps {
  open: boolean
  contentId: number
}

export default ({ open, contentId }: UseContentInvitesProps) => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const { mainApi } = useApi()

  const invitesQuery = useQuery({
    queryKey: [InvitesQueryKeys.ContentInvites, contentId],
    queryFn: () => mainApi.invites.getContentInvitesAsync(contentId),
    enabled: open,
  })

  const cancelInviteMutation = useMutation({
    mutationFn: (inviteId: number) => mainApi.invites.cancelInviteAsync(inviteId),
    onSuccess: (_, inviteId) => {
      queryClient.setQueryData<InviteWithQualificationsResponse[]>(
        [InvitesQueryKeys.ContentInvites, contentId],
        (prevInvites) => prevInvites?.filter((invite) => invite.Id !== inviteId)
      )
    },
  })

  const onCopyInviteLink = async (inviteId: number) => {
    try {
      const {
        response: { data: inviteLink },
      } = await callApiAsync(getInviteUrlById.request({ Id: inviteId }))

      // eslint-disable-next-line compat/compat
      await navigator.clipboard.writeText(inviteLink)

      dispatch(onAddSuccessNotification({ message: copyInviteLinkSuccessMessage }))
    } catch {
      dispatch(onAddErrorNotification({ message: copyInviteLinkFailMessage }))
    }
  }

  const onResendInvite = async (inviteId: number) => {
    try {
      await callApiAsync(resendInvite.request({ Id: inviteId }))

      dispatch(onAddSuccessNotification({ message: resendInviteSuccessMessage }))
    } catch {
      dispatch(onAddErrorNotification({ message: resendInviteFailMessage }))
    }
  }

  const onRevokeQuickAccessLink = async (inviteId: number) => {
    await callApiAsync(revokeQuickAccessUrl.request({ Id: inviteId }))

    queryClient.setQueryData<InviteWithQualificationsResponse[]>(
      [InvitesQueryKeys.ContentInvites, contentId],
      (prevInvites) =>
        prevInvites?.map((invite) => (invite.Id === inviteId ? { ...invite, IdentityProtected: false } : invite))
    )
  }

  const onSetQuickAccessLinkPassword = async ({ Id, Password }: { Id: number; Password: string }) => {
    await callApiAsync(updateQuickAccessUrlPassword.request({ Id, Password }))

    queryClient.setQueryData<InviteWithQualificationsResponse[]>(
      [InvitesQueryKeys.ContentInvites, contentId],
      (prevInvites) =>
        prevInvites?.map((invite) => (invite.Id === Id ? { ...invite, IdentityProtected: true } : invite))
    )
  }

  const onUpdateInviteRole = async ({ Id, Role }: { Id: number; Role: ContentUserRole }) => {
    await callApiAsync(updateInviteRole.request({ Id, Role }))

    queryClient.setQueryData<InviteWithQualificationsResponse[]>(
      [InvitesQueryKeys.ContentInvites, contentId],
      (prevInvites) => prevInvites?.map((invite) => (invite.Id === Id ? { ...invite, Role } : invite))
    )
  }

  const onUpdateInviteTime = async ({ Id, StartDate, EndDate }: { Id: number; StartDate: string; EndDate: string }) => {
    await callApiAsync(updateInviteTime.request({ Id, StartDate, EndDate }))

    queryClient.setQueryData<InviteWithQualificationsResponse[]>(
      [InvitesQueryKeys.ContentInvites, contentId],
      (prevInvites) =>
        prevInvites?.map((invite) => {
          if (invite.Id === Id) {
            const newInvite = { ...invite }
            newInvite.StartDate = StartDate
            newInvite.EndDate = EndDate
            return { ...newInvite }
          }

          return invite
        })
    )
  }

  const sendInvitesMutation = useMutation({
    mutationFn: (invites: InvitesCreateRequest) => mainApi.invites.sendInvitesAsync(invites),
    onSuccess: (invites) => {
      queryClient.setQueryData<InviteWithQualificationsResponse[]>(
        [InvitesQueryKeys.ContentInvites, contentId],
        (prevInvites) => [...(prevInvites || []), ...invites]
      )
      dispatch(onAddSuccessNotification({ message: inviteSuccessMessage }))
    },
    onError: (error: AxiosError) => {
      const message = getErrorMessageFromResponse(error?.response?.data)

      dispatch(onAddErrorNotification({ message }))
    },
  })

  return {
    loaded: !invitesQuery.isLoading && !invitesQuery.isError,
    invites: invitesQuery.data || [],
    onCancelInvite: cancelInviteMutation.mutateAsync,
    onCopyInviteLink,
    onResendInvite,
    onRevokeQuickAccessLink,
    onSetQuickAccessLinkPassword,
    onSendInvites: sendInvitesMutation.mutateAsync,
    onUpdateInviteTime,
    onUpdateInviteRole,
  }
}
